import {
  Footer,
  type FooterProps,
} from 'fitify-ui-onboarding/src/components/Paywall/Footer/Footer'
import { Header } from 'fitify-ui-onboarding/src/components/Paywall/Header/Header'
import { useEmailUserContext } from 'fitify-ui-onboarding/src/contexts/EmailUserContext'
import { useFeatureValue } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import { useTranslation } from 'next-i18next/pages'
import { useEffect, useMemo, useState } from 'react'

import { CookieBotStyles } from 'components/CookieBotStyles/CookieBotStyles'
import { LayoutContent } from 'components/GenderFunnel/Layout/Layout.Styled'
import { PageBackground } from 'components/GenderFunnel/Layout/PageBackground/PageBackground'
import { useLayoutTheme } from 'components/GenderFunnel/Layout/useLayoutTheme'
import { ThemeProvider } from 'components/ThemeProvider'
import { footerLinks } from 'data/navigation'

import { LayoutWrapper } from './Layout.Styled'
import type { LayoutProps } from './Layout.Types'

export const Layout = ({
  children,
  headerRef,
  logoLink,
  onCountdownCtaClick,
  pageConfig = {},
}: LayoutProps) => {
  const { t } = useTranslation()
  const { emailUser } = useEmailUserContext()
  const hasCountdown = Boolean(pageConfig?.countdownExpirationMillis)
  const backgroundStyle = useFeatureValue('onboarding_background_style')

  const [headerHeight, setHeaderHeight] = useState<number | undefined>(
    undefined
  )

  useEffect(() => {
    if (!headerRef?.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHeaderHeight(entry.contentRect.height)
      }
    })

    resizeObserver.observe(headerRef.current)
    setHeaderHeight(headerRef.current.getBoundingClientRect().height)

    return () => resizeObserver.disconnect()
  }, [headerRef])

  const links = useMemo<FooterProps['links']>(() => {
    return [
      {
        href: footerLinks.privacyPolicy.href,
        target: '_blank',
        rel: 'noreferrer',
        text: t(footerLinks.privacyPolicy.translate),
      },
      {
        href: footerLinks.terms.href,
        target: '_blank',
        rel: 'noreferrer',
        text: t(footerLinks.terms.translate),
      },
    ]
  }, [t])

  const theme = useLayoutTheme()

  return (
    <ThemeProvider backgroundStyle={backgroundStyle} theme={theme}>
      <CookieBotStyles />
      <PageBackground backgroundStyle={backgroundStyle} />
      <Header
        backgroundStyle={backgroundStyle}
        containerRef={headerRef}
        emailUser={emailUser}
        logoLink={logoLink}
        onCountdownCtaClick={onCountdownCtaClick}
        pageConfig={pageConfig}
      />
      <LayoutWrapper $hasCountdown={hasCountdown} $headerHeight={headerHeight}>
        <LayoutContent $variant="subscription">
          {children}
          <Footer links={links} />
        </LayoutContent>
      </LayoutWrapper>
    </ThemeProvider>
  )
}
